import clsx from 'clsx'
import {
  type Control,
  Controller,
  type FieldError,
  type FieldValues,
  type Path
} from 'react-hook-form'
import { FloatLabel } from '@/components/float-label'
import { InputErrorText } from '@/components/input-error-text'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

interface Option {
  id: string
  label: string
}

interface SelectInputProps<T extends FieldValues> {
  control?: Control<T>
  error?: FieldError
  name: Path<T>
  options: Option[]
  placeholder?: string
  label?: string
  variant?: 'default' | 'floating' | 'labelUp'
}

export const SelectInput = <T extends FieldValues>({
  control,
  error,
  name,
  options,
  placeholder,
  label,
  variant
}: SelectInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={undefined}
      render={({ field }) => (
        <FloatLabel label={label} variant={variant} hasValue={!!field.value}>
          <Select onValueChange={field.onChange} value={field.value}>
            <SelectTrigger
              className={clsx('h-10', 'cursor-pointer w-full', {
                'border-destructive': error
              })}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="z-10">
              {options.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InputErrorText error={error} />
        </FloatLabel>
      )}
    />
  )
}
